import { NodeType } from 'shared/types/ingestion'
import {
  childNodesOfType,
  nodesOfTypeWithContentV2,
} from 'components/utility/TreeHelpers'
import { INode } from 'shared/types/tree'
import { createPresentation, presentationUpdates } from '../TreeHelpers'
import { createRegexFromFormat } from '../../../ingestion/AIGenerated/utilities/utility'
import { parseFilenameByFormat } from '../../../ingestion/AIGenerated/utilities/parseFilenameByFormat'
import { PresentationFile } from './types'
import { UpdateData } from '../nodeUpdates/types'
import { idsForNode, UpdateMethod, UpdateType } from '../nodeUpdates/utility'

export function presentationListForNode(
  node: INode,
  presentationNodeTitle: string,
) {
  const lists = childNodesOfType(node, NodeType.NodeList)
  return lists.find((node) => node.title.includes(presentationNodeTitle))
}

function getPresentationDescription(lesson: INode) {
  const trimmedTitle = lesson.title.split(':')[1].trim()
  return `Teach, ${trimmedTitle} to your students with this slide presentation. If you want to edit these slides click on the Edit toggle and then click on the presentation. If you just want to browse the slides, you can click directly on the presentation.`
}

function getPresentationLink(file: { id: string }) {
  return `https://docs.google.com/presentation/d/${file.id}/preview`
}

function updateForExistingPresentationList(
  presentationList: INode,
  file: { id: string; name: string },
  lesson: INode,
): UpdateData[] {
  const [presentationNode] = childNodesOfType(
    presentationList,
    NodeType.Presentation,
  )
  const presentationData = {
    description: getPresentationDescription(lesson),
    kiddomLink: getPresentationLink(file),
    presentationTitle: lesson.title,
  }
  if (!presentationNode) {
    const presentation = createPresentation(presentationData)
    return [
      {
        type: UpdateType.node,
        ids: idsForNode(presentationList),
        params: presentation,
        method: UpdateMethod.create,
      },
    ]
  }
  return presentationUpdates(presentationNode, presentationData)
}

export function getPresentationUpdatesForFile(
  file: { id: string; name: string },
  tree: INode,
  {
    customFormatFileName,
    presentationListTitle,
  }: {
    customFormatFileName: string
    presentationListTitle: string
  },
): UpdateData[] {
  const regexFilterFilename = createRegexFromFormat(customFormatFileName)
  const filenameData = parseFilenameByFormat(
    file.name,
    regexFilterFilename,
    customFormatFileName,
  )
  if (!filenameData) {
    return []
  }
  const lesson = getNodeForFile(file, customFormatFileName, tree)

  if (!lesson) {
    return []
  }

  const presList = presentationListForNode(lesson, presentationListTitle)

  if (presList) {
    return updateForExistingPresentationList(presList, file, lesson)
  }

  return [
    {
      type: UpdateType.node,
      ids: idsForNode(lesson),
      params: {
        ...createPresentation(
          {
            description: getPresentationDescription(lesson),
            kiddomLink: getPresentationLink(file),
            presentationTitle: lesson.title,
          },
          presentationListTitle,
        ),
        next_sibling_node_id: lesson.nodes[0].id,
      },
      method: UpdateMethod.create,
    },
  ]
}

export function getPresentationUpdates(
  gDriveData: PresentationFile[],
  tree: INode,
  {
    customFormatFileName,
    presentationListTitle,
  }: {
    presentationListTitle: string
    customFormatFileName: string
  },
) {
  return gDriveData.reduce<UpdateData[]>((acc, file: PresentationFile) => {
    return [
      ...acc,
      ...getPresentationUpdatesForFile(file, tree, {
        customFormatFileName,
        presentationListTitle,
      }),
    ]
  }, [])
}

export function getFileIndexMapping({
  fileName,
  fileFormat,
}: {
  fileName: string
  fileFormat: string
}) {
  const fileNameRegex = createRegexFromFormat(fileFormat)
  const typeParts = [...fileFormat.matchAll(/{(.*?)}/g)]
    .map((match) => match[1]) // extract the capture group inside {}
    .filter(
      (_, i, arr) =>
        fileFormat.indexOf(`{${arr[i]}}`) < fileFormat.indexOf('{name}'),
    )

  const match = fileName.match(fileNameRegex)

  return typeParts.reduce<[string, string | undefined][]>(
    (acc, type, index) => {
      const indexMatch = match?.[index + 1]
      if (Number(indexMatch)) {
        return [...acc, [type, String(Number(indexMatch))]]
      }
      return [...acc, [type, String(indexMatch)]]
    },
    [],
  )
}

export function getNodeForMapping(
  mapping: [string, string | undefined][],
  tree: INode,
) {
  const titleCheck = (index: string, nodeType: string, node: INode) => {
    const titleRegex = new RegExp(
      `${nodeType}\\s+(\\d+)(?=\\s*[:\\- ]\\s*|\\s|$)`,
    )
    const match = node.title.toLowerCase().match(titleRegex)
    return !!match && match[1] === index
  }
  return mapping.reduce<INode | undefined>((acc, [nodeType, index]) => {
    if (nodeType === 'grade' || !acc) return acc
    const lookFor =
      nodeType === 'lesson'
        ? ['lesson_plan']
        : nodeType === 'module'
          ? ['module', 'unit']
          : [nodeType]
    const found = nodesOfTypeWithContentV2({
      tree: acc,
      types: lookFor,
      extraCheck: (n) => {
        return !!index && !!acc && titleCheck(index, nodeType, n)
      },
    })
    return found[0]
  }, tree)
}

export function getNodeForFile(
  file: { name: string },
  fileFormat: string,
  tree: INode,
) {
  const indexMapping = getFileIndexMapping({
    fileName: file.name,
    fileFormat,
  })
  return getNodeForMapping(indexMapping, tree)
}
